/* ===================== ASTROLOGER KUNDLI STUDIO =====================
   Role-specific Kundli screen for astrologer accounts, rendered through
   TAB_ROLE_COMPONENTS in tabs/index.js — the seeker KundliTab (studio on
   the saved profile) stays untouched for every other role.

   Astrologers cast charts FOR CLIENTS: pick a client from their live
   bookings (react-select) to prefill name, date & time of birth — the
   birth PLACE is entered manually via the live location autocomplete so
   the astrologer always confirms the exact city (and its coordinates).
   The full Kundli Studio then opens for that client. Every chart cast is
   saved to the astrologer's account (admin-v2 /kundlis, linked to the
   client's booking) — the Saved Charts list re-casts any of them live. */
import { useMemo, useState } from 'react'
import Select from 'react-select'
import { DateField, TimeField } from '@astro/date-field'
import { KundliStudio, LocationSelect, SavedKundlis, savedKey, savedToBirth } from '@astro/kundli-studio'
import { selectStyles } from '@astro/astrologer-forms'
import { useAstrologerBookings } from '../useDashboard'

const inputCls =
  'w-full bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50'

const BLANK_FORM = { name: '', dob: '', tob: '' }

export default function AstrologerKundliTab() {
  const { bookings, isLoading: bookingsLoading } = useAstrologerBookings()
  const [form, setForm] = useState(BLANK_FORM)
  const [location, setLocation] = useState(null) // picked birth place (exact coords)
  const [clientKey, setClientKey] = useState(null)
  const [bookingId, setBookingId] = useState(null) // the picked client's booking, linked on save
  const [error, setError] = useState(null)
  const [active, setActive] = useState(null) // the client whose studio is open
  const [showForm, setShowForm] = useState(true)
  const [showSaved, setShowSaved] = useState(false) // saved list while a studio is open

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  /** Wipe the form back to a clean slate — fields, place, client pick and error. */
  const resetForm = () => {
    setForm(BLANK_FORM)
    setLocation(null)
    setClientKey(null)
    setBookingId(null)
    setError(null)
  }

  // One option per DISTINCT client — a regular client books many sessions but
  // must appear once. Identity is email/mobile (name only as a last resort,
  // lowercased). The mobile/email suffix on the label keeps two different
  // clients who share a name distinguishable.
  const clientOptions = useMemo(() => {
    const byClient = new Map()
    for (const b of bookings) {
      const name = b.client || b.person
      if (!name) continue
      const key = String(b.clientEmail || b.clientMobile || name).toLowerCase()
      const prev = byClient.get(key)
      if (!prev || (!prev.dob && b.birthDate)) {
        byClient.set(key, {
          value: key,
          label: [name, b.clientMobile || b.clientEmail].filter(Boolean).join(' · '),
          name,
          dob: b.birthDate,
          tob: b.birthTime,
          bookingId: b.id,
        })
      }
    }
    return [...byClient.values()]
  }, [bookings])
  const selectedClient = clientOptions.find((o) => o.value === clientKey) ?? null

  // Prefill name, date & time from the booking — but NOT the place; the
  // astrologer enters (and confirms) the birth city manually below.
  const pickClient = (option) => {
    setClientKey(option?.value ?? null)
    setBookingId(option?.bookingId ?? null)
    setError(null)
    setLocation(null)
    if (!option) return
    setForm({ name: option.name || '', dob: option.dob || '', tob: option.tob || '' })
  }

  const submit = (e) => {
    e.preventDefault()
    if (!form.dob) { setError("Please enter the client's date of birth."); return }
    if (!location) { setError("Please search and select the client's birth place."); return }
    setError(null)
    const entry = {
      name: form.name.trim(),
      dob: form.dob,
      tob: form.tob || '12:00',
      place: location.Name,
      location,
      bookingId,
    }
    setActive(entry)
    setShowForm(false)
  }

  // Re-open a saved chart — the studio re-casts it live from the stored
  // birth details and exact coordinates.
  const openSaved = (row) => {
    const b = savedToBirth(row)
    setActive({ name: b.name, dob: b.dob, tob: b.tob, place: b.pob, location: b.location, bookingId: row.booking_id })
    setShowForm(false)
    setShowSaved(false)
  }
  const activeSavedKey = active
    ? savedKey({ dob: active.dob, tob: active.tob, lat: active.location?.Latitude, lon: active.location?.Longitude, name: active.name })
    : ''

  return (
    <div className="space-y-6">
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <span className="eyebrow text-amber-700">Astrologer Console</span>
            <h3 className="text-xl text-astroDark font-royal">Client Kundli Studio</h3>
            <p className="text-xs text-stone-500 mt-1">
              Divisional charts, dashas, strengths, yogas &amp; the full life report for any client · Lahiri Ayanamsa
            </p>
          </div>
          {active && !showForm && (
            <div className="flex flex-wrap gap-2 self-start sm:self-auto">
              <button
                onClick={() => setShowSaved((v) => !v)}
                aria-expanded={showSaved}
                className={`text-[10px] font-bold uppercase tracking-wider border px-4 py-2.5 rounded-lg ${
                  showSaved ? 'text-astroDark border-goldAccent/60 bg-amber-50' : 'text-stone-500 border-stone-200 hover:bg-stone-50'
                }`}
              >
                Saved Charts
              </button>
              <button
                onClick={() => { resetForm(); setShowSaved(false); setShowForm(true) }}
                className="text-[10px] font-bold uppercase tracking-wider text-stone-500 border border-stone-200 hover:bg-stone-50 px-4 py-2.5 rounded-lg"
              >
                New Chart
              </button>
            </div>
          )}
        </div>

        {showForm && (
          <form onSubmit={submit} className="rounded-xl bg-amber-50/40 border border-amber-100 p-5">
            <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-amber-800/70 mb-1">Cast a Client&rsquo;s Kundli</p>
            <p className="text-xs text-stone-500 mb-4">Pick a booked client to prefill their name, date &amp; time of birth — then search and confirm their birth place.</p>

            <label className="block mb-3">
              <span className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">From Your Bookings (optional)</span>
              <Select
                inputId="kundli-client"
                options={clientOptions}
                value={selectedClient}
                onChange={pickClient}
                placeholder={bookingsLoading ? 'Loading your bookings…' : 'Select a client…'}
                styles={selectStyles}
                isClearable
                isLoading={bookingsLoading}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => 'No booked clients yet'}
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="block">
                <span className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Client Name</span>
                <input type="text" value={form.name} onChange={set('name')} placeholder="e.g. Aarav Sharma" className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Date of Birth</span>
                <DateField value={form.dob} onChange={set('dob')} className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Time of Birth</span>
                <TimeField value={form.tob} onChange={set('tob')} className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Place of Birth</span>
                <LocationSelect
                  inputId="kundli-place"
                  value={location}
                  onChange={setLocation}
                  placeholder="Type the birth city…"
                />
              </label>
            </div>

            {error && <p className="text-[11px] text-red-600 mt-3">{error}</p>}

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button type="submit" className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5">
                Open Kundli Studio
              </button>
              {active && (
                <button type="button" onClick={() => setShowForm(false)} className="text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-astroDark px-3 py-2">Cancel</button>
              )}
            </div>
          </form>
        )}

        {/* Every chart this astrologer has cast — saved to their account. */}
        {(showForm || showSaved) && (
          <div className={`kundli-studio ${showForm ? 'mt-5 pt-4 border-t border-stone-100' : ''}`}>
            <SavedKundlis onOpen={openSaved} activeKey={showForm ? '' : activeSavedKey} />
          </div>
        )}
      </div>

      {/* The full studio for the active client — driven by the picker above. */}
      {active && !showForm && (
        <KundliStudio
          birth={{ name: active.name, dob: active.dob, tob: active.tob, pob: active.place, location: active.location, bookingId: active.bookingId }}
        />
      )}
    </div>
  )
}
